import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { RelocationFlag, SexFlug } from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { labelids } from "../../assets/data/TkExplanationProperty";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkContractorAndNumberLine from "../../components/molecules/TkContractorAndNumberLine";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import TkFakeTableLine from "../../components/atoms/TkFakeTableLine";
import TkButton from "../../components/atoms/TkButton";
import TkDataTable from "../../components/molecules/TkDataTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import { IAPI_STA009_Response } from "../../type/apiType";
import { httpAPI_FAC019, httpAPI_STA009, httpAPI_STA030 } from "./api/request";
import {
  infoHeadTitleList,
  searchFormList,
  searchFormSchema,
  steps,
} from "./parts";
import "./_style.scss";
import TkSearchForm from "../../components/molecules/TkSearchForm";
import TkStepper from "../../components/atoms/TkStepper";
import { TkGetCode } from "../../utils/TkGetCode";
import dayjs from "dayjs";

/**
 * SCRC060111 職員一覧　配置換え登録（施設新設）
 * @return SCRC060111 職員一覧　配置換え登録（施設新設）
 */
const SCRC060111 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  let locationState = location.state;

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState } = useContext(BusinessContext);

  const isKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;
  const contractorNo = isKiko
    ? businessState.common?.contractorNo
    : state.authInfo?.contractorNo;

  const [facilityList, setFacilityList] = useState<any>([]);
  const [bodyList, setBodyList] = useState<any>([]);
  const [selectFacility, setSelectFacility] = useState("");
  const [selectItem, setSelectItem] = useState<
    { staffNo: string; updateDateTime: string }[]
  >([]);
  const [resData, setResData] = useState<IAPI_STA009_Response>({});

  async function handleCallApiInit() {
    // プルダウン用有効施設一覧参照API呼び出し
    try {
      const res = await httpAPI_FAC019({
        contractorNo: contractorNo,
        businessType: businessState.common?.businessType!,
        relocationFlag: RelocationFlag["配置換え元"],
      });

      if (res.resultCd === "I0000") {
        const facilityInfoList: any = res.facilityList?.map((item) => ({
          label: (item.facilityNo ?? "") + ":" + (item.facilityName ?? ""),
          value: item.facilityNo,
        }));
        facilityInfoList.push({ label: "999:契約対象外施設", value: "999" });

        setFacilityList(facilityInfoList);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  }

  useEffect(() => {
    handleCallApiInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallApi = async (value: any, type: "select" | "search") => {
    // 配置換え職員一覧参照API呼び出し
    try {
      const res = await httpAPI_STA009({
        contractorNo: contractorNo,
        facilityNo: type === "select" ? value : selectFacility,
        staffNameKanji: type === "search" ? value : "",
        businessType: businessState.common?.businessType,
        applyId: locationState.applyId,
        removeFacilityNo:
          locationState?.facilityNo ?? businessState.job_15?.facilityNo,
      });

      if (res.resultCd === "I0000") {
        const bodyInfoList = res.staffList?.map((item) => [
          {
            children:
              (item?.staffNo ?? "") + ":" + (item?.staffNameKanji ?? ""),
            disabledCheckBox: item.lockStatus !== "0",
            forSort: item?.staffNo,
          },
          {
            children: item.birthday,
            forSort: item.birthday,
            align: "center",
          },
          {
            children: TkGetCode(SexFlug, item.sex),
            forSort: item.sex,
            align: "center",
          },
          {
            children: item.firstJoinDate,
            forSort: item.firstJoinDate,
            align: "center",
          },
        ]);
        setBodyList(bodyInfoList);

        setResData(res);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const registerClick = async () => {
    // 施設新設職員異動更新API呼び出し
    try {
      const res = await httpAPI_STA030({
        contractorNo: contractorNo,
        bFacilityNo: selectFacility,
        aFacilityNo: businessState.job_15?.facilityNo,
        applyId: locationState?.applyId,
        transferDate: businessState.job_15?.pastContractCancelDate
          ? dayjs(businessState.job_15?.pastContractCancelDate)
              .add(1, "day")
              ?.format("YYYY/MM/DD")
          : businessState.job_15?.foundDate,
        staffList: selectItem,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        navigate("/SCRC050104");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const itemSelect = (item: any) => {
    if (resData.staffList && resData.staffList.length > 0) {
      const newList = item.map((i: any) => {
        return {
          staffNo: resData.staffList?.[i]?.staffNo,
          updateDateTime: resData.staffList?.[i].updateDateTime,
        };
      });
      setSelectItem(newList);
    }
  };

  return (
    <Box data-testid="SCRC060111PageForTest" className="tkSCRC060111">
      <TkStepper steps={steps} activeStep={0} />

      <TkInfoManual rule="\n">{labelids.labelid.LBLC060111_01}</TkInfoManual>

      {isKiko && (
        <TkContractorAndNumberLine
          name="契約者（法人）名"
          no={
            businessState.common?.contractorNo +
            ":" +
            businessState.common?.contractorName
          }
        />
      )}

      <TkFakeTableLine
        title="施設選択"
        type="selectForm"
        selectPropsList={facilityList}
        onGetValue={(event: any) => {
          setSelectFacility(event);
          event && handleCallApi(event, "select");
        }}
      />

      {selectFacility && (
        <TkDataTable
          headList={infoHeadTitleList}
          bodyList={bodyList}
          count={bodyList.length}
          pagin
          checkSection
          defaultRowsPerPage={10}
          onCheckSectionWidth={itemSelect}
          headerLeftComponent={
            <TkSearchForm
              listData={searchFormList}
              ruleSchema={searchFormSchema}
              onSearch={(e: any) => handleCallApi(e.name, "search")}
            />
          }
        />
      )}

      <Box className="button">
        <TkBackButton />
        <TkButton onClick={registerClick}>{"登録"}</TkButton>
      </Box>
    </Box>
  );
};

export default SCRC060111;
